import { useEffect, useState, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api, brl, pctBr } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Minus, Trash2, ShoppingCart, CreditCard, User, Search, MessageCircle, Printer, Receipt, Lock, Unlock, CheckCheck, Wallet, Wifi, WifiOff, RefreshCw, Download, Maximize2, ExternalLink, FileText, Loader2, CheckCircle2, AlertTriangle, X, ArrowLeft } from "lucide-react";
import DailyGoalWidget from "@/components/DailyGoalWidget";
import { useConnectivity, useSyncQueue, useNfceSyncQueue, useTabSyncQueue } from "@/lib/connectivity";
import { usePwaStandalone } from "@/lib/pwa";
import {
  saveBootstrap, getCachedProducts, getCachedCategories,
  getCachedCustomers, queueSale, countPendingSales,
  saveFiscalConfigCache, getFiscalConfigCache, countPendingNfces,
  getTab, queueTabMutation, deleteTab,
  saveCashSession, getCashSession, clearCashSession,
} from "@/lib/offline_db";
import { emitNfce as nfceEmit, fetchNfceBySale, openNfceCupom, NFCE_IMPRIMIVEL } from "@/lib/nfceService";


// Generate RFC4122 v4 UUID without external dep — used as idempotency key.
function newUuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Teto da gorjeta, em pontos percentuais. Espelha GORJETA_TETO_PERCENTUAL de
// backend/tip_lib.py — regra de produto, e mais restritiva que qualquer norma
// aplicável (10% do art. 141-A em SC, 15% do art. 274 da LC 214/2025 nacional).
// Se mudar aqui, mudar lá: o backend recusa, a tela só evita chegar lá.
const GORJETA_TETO_PCT = 10;

export default function POS() {
  const posUserRole = (JSON.parse(localStorage.getItem("oplyx_user") || "{}"))?.role || "owner";
  const isPosStaff = posUserRole === "staff";

  const [searchParams, setSearchParams] = useSearchParams();
  const tabId = searchParams.get("tab");
  const shouldOpenCashPrompt = searchParams.get("open-cash") === "1";
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [cart, setCart] = useState([]);
  const [customerId, setCustomerId] = useState("");
  // Sem valor padrão: obriga o caixa a escolher conscientemente a forma de
  // pagamento a cada venda, em vez de manter a última selecionada e correr
  // o risco de finalizar sem querer com o método errado.
  const [paymentMethod, setPaymentMethod] = useState("");
  const [cardKind, setCardKind] = useState("credit");
  const [cardBrand, setCardBrand] = useState("visa");
  const [cashReceived, setCashReceived] = useState("");
  const [splitOpen, setSplitOpen] = useState(false);
  const [splitPayers, setSplitPayers] = useState([]);   // array of {amount, payment_method, card_kind, card_brand, label}
  const [useCashback, setUseCashback] = useState(0);
  const [couponCode, setCouponCode] = useState("");
  // Manual checkout discount: mode `value` (R$) or `percent` (%); raw input as string for fluid typing
  // Livre pra qualquer papel (ex: troco insuficiente) — exige motivo quando quem fecha não é owner.
  const [discountMode, setDiscountMode] = useState("value");  // value | percent
  const [discountInput, setDiscountInput] = useState("");
  const [discountReason, setDiscountReason] = useState("");
  // "Consumo do colaborador" — desconto padrão fixo (não negociável) quando o próprio staff
  // está comprando produto do estabelecimento pra si. % vem de employees.discount_fixed_percent
  // (por colaborador) ou, na ausência, de settings.discount_role_percent (padrão do papel).
  const [employeePurchase, setEmployeePurchase] = useState(false);
  const [discountRolePercent, setDiscountRolePercent] = useState({});
  const [employeeDiscountPct, setEmployeeDiscountPct] = useState(null);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [tipPct, setTipPct] = useState(0);
  const [tipSettings, setTipSettings] = useState({ default_percent: 10 });
  const [peopleCount, setPeopleCount] = useState(1);
  const [channel, setChannel] = useState("balcao");
  const [lastSale, setLastSale] = useState(null);
  const [receiptPhone, setReceiptPhone] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [acquirers, setAcquirers] = useState([]);
  const [selectedAcquirerId, setSelectedAcquirerId] = useState(null);
  // NFC-e (emissor próprio)
  const [nfceDoc, setNfceDoc]         = useState(null);   // fiscal_document após emissão
  const [nfceLoading, setNfceLoading] = useState(false);
  const [fiscalConfig, setFiscalConfig] = useState(null); // { emissor_configured, nfce_disponivel, nfce_enabled }
  // "online" | "queued" (sem conexão com o backend — nota na fila local)
  const [nfceMode, setNfceMode] = useState("online");
  const [sendingReceipt, setSendingReceipt] = useState(false);
  // Tab-finalization mode state
  const [loadedTab, setLoadedTab] = useState(null);   // full tab record when tabId is set
  const [itemSplits, setItemSplits] = useState({}); // { [product_id]: dine_in_qty } — gorjeta seletiva
  // Cash session state
  const [cashSession, setCashSession] = useState(null);  // null | {status,id,opening_balance,...}
  const [openCashOpen, setOpenCashOpen] = useState(false);
  const [openingAmount, setOpeningAmount] = useState(0);
  // Close cash multi-step flow
  const [closeStep, setCloseStep] = useState(null); // null|"conference"|"success"|"divergence"|"divergence_confirm"
  const [closeActualByMethod, setCloseActualByMethod] = useState({});
  const [closeJustification, setCloseJustification] = useState("");
  const [closing, setClosing] = useState(false);
  const [closeResult, setCloseResult] = useState(null);
  // Progressive disclosure: "Pessoas" e "Canal" escondidos por default (smart defaults cobrem o caso comum)
  const [moreOptionsOpen, setMoreOptionsOpen] = useState(false);

  // Iter 117 — Offline-first POS
  const conn = useConnectivity();
  const sync = useSyncQueue(conn.online);
  const nfceSync = useNfceSyncQueue(conn.online);
  const tabSync = useTabSyncQueue(conn.online);
  // Iter 123 — POS PWA standalone (Phase 4)
  const pwa = usePwaStandalone({
    manifest: "/manifest-pos.json",
    themeColor: "#0B0D0F",
  });
  // Last bootstrap timestamp (refreshed when online)
  const [lastBootstrapAt, setLastBootstrapAt] = useState(null);
  const [installDismissed, setInstallDismissed] = useState(() =>
    typeof localStorage !== "undefined"
      ? localStorage.getItem("pos_install_dismissed_at") != null
      : false
  );

  // Iter 123 — Em modo standalone, peço persistência de storage (não eviction da IDB offline)
  useEffect(() => {
    if (pwa.isStandalone) {
      pwa.requestPersistence().then((ok) => {
        if (ok) { /* storage persistido */ }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pwa.isStandalone]);

  const load = () => {
    if (!conn.online) {
      // Offline: load from IndexedDB cache
      return Promise.all([getCachedProducts(), getCachedCategories(), getCachedCustomers()])
        .then(([p, c, cu]) => {
          setProducts((p || []).filter(x => x.active !== false));
          setCats(c || []);
          setCustomers(cu || []);
        })
        .catch(() => { /* idb empty — leave defaults */ });
    }
    // Online: bootstrap (single round-trip) + persist to IDB for offline use
    return api.get("/pos/bootstrap").then(async ({ data }) => {
      setProducts((data.products || []).filter(p => p.active !== false));
      setCats(data.categories || []);
      setCustomers(data.customers || []);
      const freshSession = data.cash_session?.status === "open" ? data.cash_session : null;
      // Preserve computed fields (expected_by_method, expected_balance) already fetched
      // by /financial/cash/current so a concurrent bootstrap can't wipe them with the raw doc.
      setCashSession(prev => {
        if (!freshSession) return null;
        if (prev?.id === freshSession.id && prev?.expected_by_method)
          return { ...freshSession, expected_by_method: prev.expected_by_method, expected_balance: prev.expected_balance };
        return freshSession;
      });
      const _branchId = localStorage.getItem("oplyx_branch");
      if (_branchId) {
        if (freshSession) saveCashSession(freshSession).catch(() => {});
        else clearCashSession(_branchId).catch(() => {});
      }
      try { await saveBootstrap(data); setLastBootstrapAt(new Date().toISOString()); }
      catch (e) { console.warn("[offline] saveBootstrap failed:", e); }
      // Tip settings still come from /auth/me (rarely changes)
      try {
        const me = await api.get("/auth/me");
        const s = me.data.settings || {};
        setTipSettings({ default_percent: s.tip_default_percent ?? 10 });
        setDiscountRolePercent(s.discount_role_percent || {});
        setEmployeeDiscountPct(me.data.discount_fixed_percent);
        const acqList = (s.acquirers || []).filter(a => a.active !== false);
        setAcquirers(acqList);
        const defAcq = acqList.find(a => a.is_default) || acqList[0];
        if (defAcq) setSelectedAcquirerId(prev => prev || defAcq.id);
      } catch { /* keep defaults */ }
    }).catch(err => {
      console.error("[pos] bootstrap falhou:", err);
      toast.error("Erro ao carregar o caixa. Verifique sua conexão.");
    });
  };
  const loadCash = async () => {
    try {
      const r = await api.get("/financial/cash/current");
      const session = r.data?.status === "open" ? r.data : null;
      setCashSession(session);
      const _branchId = localStorage.getItem("oplyx_branch");
      if (_branchId) {
        if (session) saveCashSession(session).catch(() => {});
        else clearCashSession(_branchId).catch(() => {});
      }
    } catch {
      // Offline — restaura do IDB sem zerar o estado
      const _branchId = localStorage.getItem("oplyx_branch");
      if (_branchId) {
        const cached = await getCashSession(_branchId).catch(() => null);
        if (cached) setCashSession(cached);
      }
    }
  };

  // Carrega a prontidão do emissor (empresa, certificado, módulo ativo)
  const loadFiscalConfig = () =>
    api.get("/fiscal/companies")
      .then(({ data: co }) => {
        const company = (co.companies || [])[0] || null;
        const merged = {
          emissor_configured: co.emissor?.configured ?? false,
          // Módulo fiscal ativo: decide acompanhar e imprimir a NFC-e. Sem
          // certificado, a venda mostra o motivo em vez de esconder o status.
          nfce_disponivel:    co.nfce_disponivel ?? false,
          nfce_enabled:       company?.nfce_enabled ?? false,
          environment:        co.emissor?.environment || "sandbox",
        };
        setFiscalConfig(merged);
        saveFiscalConfigCache(merged).catch(() => {});
      })
      .catch(() => setFiscalConfig({ emissor_configured: false, nfce_disponivel: false, nfce_enabled: false }));

  useEffect(()=>{
    getFiscalConfigCache().then(cached => { if (cached) setFiscalConfig(cached); }).catch(()=>{});
    load(); loadCash(); loadFiscalConfig();
  },[]);

  // Acompanha a NFC-e enquanto está "pending": a emissão roda no backend e a
  // SEFAZ responde em poucos segundos (ou o backend entra em contingência).
  useEffect(() => {
    if (!nfceDoc || nfceDoc.status !== "pending" || !nfceDoc.sale_id) return;

    const startMs = Date.now();
    const MAX_MS   = 60_000;
    let   attempt  = 0;
    const nextDelay = () => attempt < 4 ? 3_000 : 6_000;

    const poll = async () => {
      attempt++;
      try {
        const doc = await fetchNfceBySale(nfceDoc.sale_id);
        if (doc.status !== "pending") {
          setNfceDoc({ ...doc, sale_id: nfceDoc.sale_id });
          return;
        }
      } catch { /* ignora erros de rede no poll */ }

      if (Date.now() - startMs >= MAX_MS) {
        setNfceDoc(prev => prev ? { ...prev, _timedOut: true } : prev);
        return;
      }
      timer = setTimeout(poll, nextDelay());
    };

    let timer = setTimeout(poll, 2_000);
    return () => clearTimeout(timer);
  }, [nfceDoc?.sale_id, nfceDoc?.status]); // eslint-disable-line

  // Auto-print: nota autorizada (ou emitida em contingência) abre o cupom para impressão.
  const _autoPrintedNfce = useRef(null);
  useEffect(() => {
    if (!NFCE_IMPRIMIVEL.has(nfceDoc?.status) || !nfceDoc.id) return;
    if (_autoPrintedNfce.current === nfceDoc.id) return;
    _autoPrintedNfce.current = nfceDoc.id;
    openNfceCupom(nfceDoc.id, "danfe_print", "width=800,height=700");
  }, [nfceDoc?.status, nfceDoc?.id]); // eslint-disable-line

  // Iter 117 — Quando volta online, refresh do bootstrap + flush da fila.
  // Quando fica offline e cashSession é null, tenta restaurar do IDB.
  useEffect(() => {
    if (conn.online) {
      load();
      loadCash();
      sync.flushNow();
    } else {
      // Offline: se não temos sessão em memória, tenta IDB silenciosamente
      const _branchId = localStorage.getItem("oplyx_branch");
      if (_branchId && !cashSession) {
        getCashSession(_branchId)
          .then(s => { if (s) setCashSession(s); })
          .catch(() => {});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conn.online]);

  // Auto-open the "Abrir caixa" dialog when navigated with ?open-cash=1
  useEffect(() => {
    if (shouldOpenCashPrompt) {
      setOpenCashOpen(true);
      const n = new URLSearchParams(searchParams);
      n.delete("open-cash");
      setSearchParams(n, { replace: true });
    }
     
  }, [shouldOpenCashPrompt]);

  // When ?tab=<id> is present, load the tab and pre-fill the cart.
  // Handles both server IDs and offline_XXX IDs (IDB fallback).
  useEffect(() => {
    if (!tabId) { setLoadedTab(null); return; }
    const isOfflineId = tabId.startsWith("offline_");

    const applyTab = (t) => {
      setLoadedTab(t);
      setItemSplits({});
      if (t.customer_id) setCustomerId(t.customer_id);
      // Consolida lançamentos repetidos do mesmo produto (ex.: 1 água pedida na
      // chegada + mais 2 depois) numa única linha do carrinho — mesma regra do
      // fechamento (tabs.py `_merge_items_for_sale`): agrupa por produto + preço
      // unitário congelado, já que o preço embute eventuais modificadores.
      const merged = [];
      const idxByKey = {};
      for (const it of (t.items || [])) {
        if (it.paid) continue;
        const price = it.unit_price ?? it.price;
        const key = `${it.product_id}::${price}`;
        if (key in idxByKey) {
          merged[idxByKey[key]].quantity += it.quantity;
        } else {
          idxByKey[key] = merged.length;
          merged.push({ product_id: it.product_id, name: it.name, price, quantity: it.quantity });
        }
      }
      setCart(merged);
    };

    const loadFromIdb = () =>
      getTab(tabId).then(t => {
        if (t) { applyTab(t); return; }
        toast.error("Comanda não encontrada");
        setSearchParams({}, { replace: true });
      }).catch(() => {
        toast.error("Comanda não encontrada");
        setSearchParams({}, { replace: true });
      });

    if (isOfflineId) { loadFromIdb(); return; }

    api.get(`/tabs/${tabId}`).then(r => applyTab(r.data)).catch(e => {
      // Fallback to IDB (may be offline)
      getTab(tabId).then(t => {
        if (t) { applyTab(t); return; }
        toast.error(e?.response?.data?.detail || "Comanda não encontrada");
        setSearchParams({}, { replace: true });
      }).catch(() => {
        toast.error(e?.response?.data?.detail || "Comanda não encontrada");
        setSearchParams({}, { replace: true });
      });
    });
  }, [tabId, setSearchParams]);

  const exitTabMode = () => {
    setSearchParams({}, { replace: true });
    setLoadedTab(null); clear();
  };

  const openCash = async () => {
    try {
      const amount = parseFloat(openingAmount) || 0;
      await api.post("/financial/cash/open", { opening_amount: amount });
      toast.success("Caixa aberto ✅");
      setOpenCashOpen(false); setOpeningAmount(0);
      await loadCash();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Erro ao abrir caixa");
    }
  };

  const openCloseFlow = async () => {
    // Garante que cashSession tenha expected_by_method computado antes de abrir o modal.
    // Bootstrap retorna o documento raw (sem campos calculados) e pode chegar depois
    // de loadCash(), sobrescrevendo o estado com versão incompleta.
    await loadCash().catch(() => {});
    setCloseActualByMethod({});
    setCloseJustification("");
    setCloseResult(null);
    setCloseStep("conference");
  };

  const resetCloseFlow = () => {
    setCloseStep(null);
    setCloseActualByMethod({});
    setCloseJustification("");
    setCloseResult(null);
  };

  const _doClose = async (forceClose) => {
    setClosing(true);
    try {
      const payload = {
        actual_balance: parseFloat(closeActualByMethod.money) || 0,
        actual_by_method: Object.fromEntries(
          Object.entries(closeActualByMethod).map(([k, v]) => [k, parseFloat(v) || 0])
        ),
        justification: closeJustification.trim() || null,
        force_close: forceClose,
      };
      const { data } = await api.post("/financial/cash/close", payload);
      setCloseResult(data);
      setCloseStep("success");
      await loadCash();
      const _branchId = localStorage.getItem("oplyx_branch");
      if (_branchId) clearCashSession(_branchId).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Erro ao fechar caixa");
    } finally {
      setClosing(false);
    }
  };

  const handleConferenceConfirm = () => {
    const exp = cashSession?.expected_by_method || {};
    const totalDiff = Object.keys(exp).reduce((sum, k) => {
      return sum + ((parseFloat(closeActualByMethod[k]) || 0) - (parseFloat(exp[k]) || 0));
    }, 0);
    if (Math.abs(totalDiff) < 0.01) {
      _doClose(false);
    } else {
      setCloseStep("divergence");
    }
  };

  const add = (p) => {
    setCart(prev => {
      const ex = prev.find(i=>i.product_id===p.id);
      if (ex) return prev.map(i=>i.product_id===p.id?{...i, quantity:i.quantity+1}:i);
      return [...prev, { product_id:p.id, name:p.name, price:p.price, quantity:1 }];
    });
  };
  const setQty = (pid, q) => setCart(prev => prev.map(i=>i.product_id===pid?{...i, quantity:Math.max(1,q)}:i).filter(i=>i.quantity>0));
  const remove = (pid) => setCart(prev => prev.filter(i=>i.product_id!==pid));

  const total = cart.reduce((s,i)=>s + i.price*i.quantity, 0);
  const selectedCust = customers.find(c=>c.id===customerId);
  const maxCashback = selectedCust?.cashback_balance || 0;

  // % de "consumo do colaborador" disponível pra este login — colaborador cadastrado vence
  // sobre o padrão do papel. null/undefined = não configurado (toggle nem aparece).
  const fixedDiscountPct = employeeDiscountPct ?? discountRolePercent[posUserRole];
  const hasEmployeeDiscount = fixedDiscountPct !== undefined && fixedDiscountPct !== null;

  // Manual discount: livre (R$/%) pro cliente, OU o % fixo de consumo do colaborador —
  // mutuamente exclusivos. Capped to subtotal so never negative.
  const discountRaw = parseFloat(String(discountInput).replace(",", ".")) || 0;
  const manualDiscount = employeePurchase
    ? Math.min(total, Math.max(0, total * (fixedDiscountPct || 0) / 100))
    : discountMode === "percent"
      ? Math.min(total, Math.max(0, total * discountRaw / 100))
      : Math.min(total, Math.max(0, discountRaw));
  // Motivo é obrigatório pra desconto manual (não pra consumo do colaborador) quando quem
  // fecha não é owner — mesma regra que o backend aplica.
  const discountReasonRequired = !employeePurchase && manualDiscount > 0 && posUserRole !== "owner" && !discountReason.trim();
  const subtotalAfterDiscount = Math.max(0, total - manualDiscount);

  const netBeforeTip = Math.max(0, subtotalAfterDiscount - (parseFloat(useCashback)||0));
  // tippable subtotal: sum only of items whose product has has_tip=true.
  // When closing a tab, uses dine_in_qty from itemSplits (default = full quantity).
  const tippableSubtotal = cart.reduce((sum, i) => {
    const p = products.find(pp => pp.id === i.product_id);
    if (!p?.has_tip) return sum;
    if (loadedTab) {
      const dineInQty = itemSplits[i.product_id] ?? i.quantity;
      return sum + i.price * Math.min(dineInQty, i.quantity);
    }
    return sum + i.price * i.quantity;
  }, 0);
  const hasTippable = tippableSubtotal > 0;
  // TETO DE 10% — mesma regra do backend (backend/tip_lib.py): o menor entre 10%
  // dos itens que aceitam serviço e 10% da conta líquida. O segundo termo é o
  // que importa: sem ele, um desconto de cortesia deixa a gorjeta acima de 10%
  // do que o cliente pagou, e o backend recusa a venda no fechamento — com o
  // cliente na frente do balcão. Arredonda para BAIXO, senão não é teto.
  const tipCap = Math.floor(Math.min(tippableSubtotal, subtotalAfterDiscount) * GORJETA_TETO_PCT) / 100;
  const tipAmountRaw = hasTippable && tipPct>0 ? Math.round(tippableSubtotal * (tipPct/100) * 100)/100 : 0;
  const tipAmount = Math.min(tipAmountRaw, tipCap);
  const tipCapped = tipAmountRaw > tipAmount + 0.001;
  const net = netBeforeTip + tipAmount;

  // Acquirer resolution for fee preview
  const activeAcquirers = acquirers.filter(a => a.active !== false);
  const selectedAcquirer = activeAcquirers.find(a => a.id === selectedAcquirerId) || activeAcquirers[0] || null;
  const cardFeeKey = `${cardKind}_${cardBrand}`.toLowerCase();
  const acquirerFees = selectedAcquirer?.card_fees || {};
  const cardFeePct = paymentMethod === "card"
    ? (acquirerFees[cardFeeKey] ?? acquirerFees[`${cardKind}_default`] ?? 0)
    : 0;
  const cardFeeAmount = cardFeePct > 0 ? Math.round(net * cardFeePct / 100 * 100) / 100 : 0;

  // Troco: só se aplica ao pagamento em dinheiro sem divisão (cada perna do
  // split já tem seu próprio valor fechado, não faz sentido pedir "recebido").
  const isCashPayment = paymentMethod === "cash" && splitPayers.length === 0;
  const cashReceivedNum = parseFloat(cashReceived) || 0;
  const changeDue = isCashPayment ? Math.round((cashReceivedNum - net) * 100) / 100 : 0;
  const cashInsufficient = isCashPayment && cashReceivedNum > 0 && changeDue < -0.001;

  const clear = () => {
    setCart([]); setCustomerId(""); setUseCashback(0); setCouponCode("");
    setTipPct(0); setPeopleCount(1); setChannel("balcao");
    setDiscountInput(""); setDiscountReason(""); setDiscountMode("value"); setEmployeePurchase(false);
    setItemSplits({}); setCashReceived(""); setPaymentMethod("");
  };

  const checkout = async () => {
    if (isSubmitting) return;
    if (!cart.length) return toast.warning("Carrinho vazio");
    if (!paymentMethod && splitPayers.length === 0) return toast.warning("Selecione a forma de pagamento");
    if (discountReasonRequired) return toast.warning("Informe o motivo do desconto");
    if (!cashSession) {
      setOpenCashOpen(true);
      return;
    }
    setIsSubmitting(true);

    // Offline tab close: todos os pagamentos liberados.
    if (!conn.online && loadedTab) {
      try {
        const cardDetail = paymentMethod === "card"
          ? { card_kind: cardKind, card_brand: cardBrand } : {};
        const splitPayload = splitPayers.length > 0 ? { payments: splitPayers } : {};
        const mut = {
          _mutation_id: newUuid(),
          tab_id: loadedTab.id,
          type: "close_tab",
          payload: {
            payment_method: paymentMethod === "card" ? "card_offline" : paymentMethod,
            use_cashback: parseFloat(useCashback) || 0,
            tip_amount: tipAmount,
            ...cardDetail,
            ...splitPayload,
          },
        };
        await queueTabMutation(mut);
        await deleteTab(loadedTab.id);
        tabSync.refreshCount();
        const closedTotal = net;
        toast.success(
          `Comanda "${loadedTab.label}" fechada em contingência · ${brl(closedTotal)} · sincroniza quando a rede voltar`
        );
        setLastSale({
          id: `offline-tab-${newUuid().slice(0, 8)}`,
          total: closedTotal,
          customer: customers.find(c => c.id === customerId) || null,
          _offline: true,
          _tab: true,
          _tabLabel: loadedTab.label,
          change: changeDue > 0 ? changeDue : 0,
        });
        setSplitPayers([]);
        exitTabMode();
      } catch (e) {
        toast.error("Falha ao registrar fechamento offline: " + (e?.message || e));
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // Offline path — venda local, sincroniza quando rede voltar.
    // Todos os pagamentos liberados: maquininha e PIX via celular do cliente
    // operam com conectividade própria, independente do WiFi do estabelecimento.
    if (!conn.online) {
      try {
        const cardDetail = paymentMethod === "card" ? { card_kind: cardKind, card_brand: cardBrand } : {};
        const splitPayload = splitPayers.length > 0 ? { payments: splitPayers } : {};
        const idempotency_key = newUuid();
        const client_created_at = new Date().toISOString();
        const effMethod = paymentMethod === "card" ? "card_offline" : paymentMethod;
        const payload = {
          customer_id: customerId || null,
          items: cart.map(i => ({ product_id: i.product_id, quantity: i.quantity })),
          payment_method: effMethod,
          use_cashback: parseFloat(useCashback) || 0,
          coupon_code: couponCode || null,
          tip_amount: tipAmount,
          people_count: Math.max(1, parseInt(peopleCount) || 1),
          channel,
          manual_discount: Number(manualDiscount.toFixed(2)),
          manual_discount_reason: manualDiscount > 0 ? discountReason : "",
          employee_purchase: employeePurchase,
          ...cardDetail,
          ...splitPayload,
          idempotency_key,
          client_created_at,
          offline_origin: true,
        };
        await queueSale(payload);
        sync.refreshCount();
        const total = cart.reduce((s, i) => s + i.price * i.quantity, 0) + tipAmount - manualDiscount;
        toast.success(`Venda em contingência · ${brl(total)} · será transmitida quando a rede voltar.`);
        setSplitPayers([]);
        setLastSale({ id: `offline-${idempotency_key.slice(0, 8)}`, total, customer: customers.find(c => c.id === customerId) || null, _offline: true, change: changeDue > 0 ? changeDue : 0 });
        clear();
      } catch (e) {
        toast.error("Falha ao gravar venda local: " + (e?.message || e));
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    try {
      const cardDetail = paymentMethod === "card" ? { card_kind: cardKind, card_brand: cardBrand } : {};
      const splitPayload = splitPayers.length > 0 ? { payments: splitPayers } : {};
      let saleData;
      if (loadedTab) {
        // Persistir no tab itens adicionados no POS que ainda não estão no banco
        const origQtyByPid = {};
        (loadedTab.items || []).filter(it => !it.paid).forEach(it => {
          origQtyByPid[it.product_id] = (origQtyByPid[it.product_id] || 0) + (it.quantity || 0);
        });
        const cartQtyByPid = {};
        cart.forEach(it => {
          cartQtyByPid[it.product_id] = (cartQtyByPid[it.product_id] || 0) + (it.quantity || 0);
        });
        const newItems = Object.entries(cartQtyByPid)
          .map(([pid, qty]) => ({ product_id: pid, delta: qty - (origQtyByPid[pid] || 0) }))
          .filter(x => x.delta > 0)
          .map(x => ({ product_id: x.product_id, quantity: x.delta }));
        if (newItems.length > 0) {
          await api.post(`/tabs/${loadedTab.id}/items`, newItems);
        }

        const idempotency_key = newUuid();
        const { data } = await api.post(`/tabs/${loadedTab.id}/close`, {
          payment_method: paymentMethod,
          use_cashback: parseFloat(useCashback) || 0,
          tip_amount: tipAmount,
          manual_discount: Number(manualDiscount.toFixed(2)),
          manual_discount_reason: manualDiscount > 0 ? discountReason : "",
          employee_purchase: employeePurchase,
          ...cardDetail,
          ...splitPayload,
          idempotency_key,
          acquirer_id: selectedAcquirerId || undefined,
        });
        saleData = data.sale;
        toast.success(`Comanda "${loadedTab.label}" finalizada: ${brl(saleData.total_amount)} em ${data.duration_minutes}min`);
      } else {
        const idempotency_key = newUuid();
        const { data } = await api.post("/sales", {
          customer_id: customerId || null,
          items: cart.map(i=>({ product_id:i.product_id, quantity:i.quantity })),
          payment_method: paymentMethod,
          use_cashback: parseFloat(useCashback)||0,
          coupon_code: couponCode || null,
          tip_amount: tipAmount,
          people_count: Math.max(1, parseInt(peopleCount) || 1),
          channel: channel,
          manual_discount: Number(manualDiscount.toFixed(2)),
          manual_discount_reason: manualDiscount > 0 ? discountReason : "",
          employee_purchase: employeePurchase,
          ...cardDetail,
          ...splitPayload,
          idempotency_key,
          acquirer_id: selectedAcquirerId || undefined,
        });
        saleData = data.sale;
        toast.success(`Venda ${brl(saleData.total_amount)} registrada.${saleData.cashback_generated>0?` Cashback: ${brl(saleData.cashback_generated)}`:""}${tipAmount>0?` Gorjeta: ${brl(tipAmount)}`:""}`);
      }
      setSplitPayers([]);
      const selected = customers.find(c=>c.id===customerId);
      setLastSale({ id: saleData.id, total: saleData.total_amount, customer: selected || null, change: changeDue > 0 ? changeDue : 0, ...(loadedTab ? { _tab: true, _tabLabel: loadedTab.label } : {}) });
      setReceiptPhone(selected?.phone || "");
      // Cache de config de versão anterior não tem nfce_disponivel.
      if (fiscalConfig?.nfce_disponivel ?? fiscalConfig?.emissor_configured) emitNfce(saleData.id);
      window.dispatchEvent(new Event("sale-completed"));
      if (loadedTab) exitTabMode();
      else clear();
      load(); loadCash();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "cash_closed") {
        toast.error(d.message || "Caixa fechado");
        setOpenCashOpen(true);
      } else {
        toast.error((typeof d === "string" ? d : d?.message) || "Erro");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Iter 87: receipt WhatsApp send removed; operator prints or shares the
  // public link manually. Kept the printReceipt path.

  const printReceipt = () => {
    if (!lastSale) return;
    window.open(`/receipt/${lastSale.id}?auto=1`, "receipt", "width=380,height=640");
  };

  const emitNfce = async (autoSaleId = null) => {
    const _saleId = autoSaleId || lastSale?.id;
    if (!_saleId) return;
    if (!autoSaleId && lastSale?._offline) {
      toast.warning("Essa venda foi registrada offline. A NFC-e será emitida automaticamente quando sincronizar.");
      return;
    }
    setNfceLoading(true);
    try {
      const result = await nfceEmit({ saleId: _saleId, onStatusChange: setNfceMode });
      if (result.source === "online") {
        const doc = result.document;
        setNfceDoc(doc ? { ...doc, sale_id: _saleId } : { status: "pending", sale_id: _saleId });
        if (doc?.status === "authorized") toast.success("NFC-e autorizada pela SEFAZ");
      } else {
        toast.warning("Sem conexão. NFC-e salva na fila local e será enviada automaticamente");
        nfceSync.refreshCount();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao emitir NFC-e");
    } finally {
      setNfceLoading(false);
    }
  };

  const filtered = products.filter(p=>(catFilter==="all"||p.category_id===catFilter) && (!q || (p.name||"").toLowerCase().includes(q.toLowerCase()) || (p.sku||"").toLowerCase().includes(q.toLowerCase())));

  // Iter 124 — Detecta se já estamos na rota /pos/standalone (esconde o botão "Abrir em nova janela")
  const isStandaloneRoute = typeof window !== "undefined" &&
    window.location.pathname === "/pos/standalone";

  const openInNewWindow = () => {
    const w = window.open(
      "/pos/standalone",
      "oplyx-pos-standalone",
      "width=1280,height=800,menubar=no,toolbar=no,location=no,status=no"
    );
    if (!w) {
      toast.error("Pop-up bloqueado pelo navegador. Permita pop-ups para este site e tente novamente.");
    }
  };

  return (
    <div className="space-y-4" data-testid="pos-page">
      {/* Iter 124 — Botão "Abrir caixa em nova janela" (esconde quando JÁ está na janela standalone) */}
      {!isStandaloneRoute && (
        <div className="flex items-center justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={openInNewWindow}
            className="border-emerald-400 text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
            data-testid="pos-open-standalone-btn"
            title="Abre o caixa em uma janela dedicada, sem menu lateral, ideal para o operador"
          >
            <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
            Abrir caixa em nova janela
          </Button>
        </div>
      )}
      {/* Iter 123 — PWA install banner (POS Phase 4 / standalone window) */}
      {pwa.installPrompt && !pwa.isStandalone && !installDismissed && (
        <div
          className="flex items-center gap-3 px-4 py-2.5 rounded-lg border bg-gradient-to-r from-emerald-50 via-emerald-50 to-white border-emerald-300 text-emerald-900 text-sm"
          data-testid="pos-pwa-install-banner"
        >
          <Maximize2 className="w-4 h-4 text-emerald-700 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold">Instalar POS como app</div>
            <div className="text-[11px] opacity-80 truncate">
              Roda em janela própria, sem barra do navegador, com atalhos para Vendas/Comandas/Caixa.
              {pwa.isStandalone && " Você já está em modo standalone."}
            </div>
          </div>
          <Button
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 shrink-0"
            onClick={async () => {
              const ok = await pwa.doInstall();
              if (ok) toast.success("POS instalado · abra pelo ícone na próxima vez");
            }}
            data-testid="pos-pwa-install-btn"
          >
            <Download className="w-3.5 h-3.5 mr-1" />Instalar
          </Button>
          <button
            onClick={() => {
              localStorage.setItem("pos_install_dismissed_at", String(Date.now()));
              setInstallDismissed(true);
            }}
            className="text-[11px] opacity-50 hover:opacity-100 px-1"
            title="Não mostrar novamente"
            data-testid="pos-pwa-install-dismiss"
          >
            ✕
          </button>
        </div>
      )}

      {/* Iter 117 — Connectivity banner: visible at all times so the operator
          knows whether sales are going to the queue. */}
      <div
        className={`flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg border text-sm ${
          conn.online
            ? "bg-emerald-50 border-emerald-200 text-emerald-900"
            : "bg-red-50 border-red-200 text-red-900"
        }`}
        data-testid="pos-connectivity-banner"
      >
        <div className="flex items-center gap-2.5">
          {conn.online ? (
            <Wifi className="w-4 h-4 text-emerald-600" />
          ) : (
            <WifiOff className="w-4 h-4 text-red-600 animate-pulse" />
          )}
          <span className="font-semibold">
            {conn.online ? "Online" : "Operando offline"}
          </span>
          {pwa.isStandalone && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-700 text-white text-[10px] rounded-full font-bold tracking-wider"
              title="Rodando como aplicativo (PWA standalone), modo dedicado"
              data-testid="pos-pwa-standalone-pill"
            >
              <Maximize2 className="w-2.5 h-2.5" /> PWA
            </span>
          )}
          {conn.online && conn.latencyMs != null && (
            <span className="text-[11px] font-mono opacity-70">{conn.latencyMs}ms</span>
          )}
          {!conn.online && (
            <span className="text-[12px] opacity-90">
              · vendas serão enfileiradas e sincronizadas quando a rede voltar
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-[12px]">
          {sync.pendingCount > 0 && (
            <span
              className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-amber-100 border border-amber-300 text-amber-900 rounded-full font-semibold"
              data-testid="pos-pending-count"
            >
              {sync.syncing && <RefreshCw className="w-3 h-3 animate-spin" />}
              {sync.pendingCount} venda{sync.pendingCount > 1 ? "s" : ""} pendente{sync.pendingCount > 1 ? "s" : ""}
            </span>
          )}
          {conn.online && sync.pendingCount > 0 && (
            <Button size="sm" variant="outline" className="h-6 text-[11px]"
              onClick={() => sync.flushNow()} data-testid="pos-flush-now-btn">
              Sincronizar agora
            </Button>
          )}
        </div>
        {/* NFC-e contingency badge — only when fiscal is enabled */}
        {fiscalConfig?.nfce_enabled && (
          <span
            data-testid="pos-nfce-mode-badge"
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold
              ${nfceMode === "online"
                ? "bg-green-900/40 text-green-300 border border-green-800"
                : "bg-red-900/40 text-red-300 border border-red-800"
              }`}
          >
            {nfceMode === "online" && (
              <><CheckCircle2 className="w-3 h-3" /> NF-e online</>
            )}
            {nfceMode === "queued" && (
              <><AlertTriangle className="w-3 h-3" /> {nfceSync.pendingCount} nota{nfceSync.pendingCount !== 1 ? "s" : ""} na fila</>
            )}
          </span>
        )}
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">Caixa (POS)</h1>
          <p className="text-sm text-muted-foreground">
            {loadedTab
              ? <>Finalizando comanda <b>"{loadedTab.label}"</b>: venda, pagamento e caixa em um único fluxo.</>
              : "Venda rápida com produtos"}
          </p>
        </div>
        <DailyGoalWidget compact />
      </div>

      {loadedTab && (
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-2.5 flex items-center justify-between gap-3"
             data-testid="pos-tab-banner">
          <div className="flex items-center gap-2 text-sm text-emerald-900">
            <Receipt className="w-4 h-4" />
            <b>Comanda {loadedTab.label}</b> · {loadedTab.items?.length || 0} itens · {brl(loadedTab.total || 0)}
          </div>
          <Button variant="ghost" size="sm" onClick={exitTabMode} data-testid="pos-exit-tab">
            Cancelar e voltar
          </Button>
        </div>
      )}

      {!cashSession && (
        <div className="rounded-xl bg-amber-50 border border-amber-300 px-4 py-3 flex items-center justify-between gap-3"
             data-testid="pos-cash-closed-banner">
          <div className="flex items-start gap-3">
            <Lock className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-amber-900">Caixa fechado</div>
              <div className="text-xs text-amber-800">
                Abra o caixa para registrar vendas e finalizar comandas.
                Toda venda gera automaticamente um movimento de caixa.
              </div>
            </div>
          </div>
          <Button onClick={() => setOpenCashOpen(true)} data-testid="pos-open-cash-btn"
                  className="rounded-full bg-amber-600 hover:bg-amber-700">
            Abrir caixa
          </Button>
        </div>
      )}

      {cashSession && (
        <div className="rounded-xl bg-emerald-50 border border-emerald-300 px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
             data-testid="pos-cash-open-banner">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <Unlock className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <div className="font-semibold text-emerald-900 truncate">
                Caixa aberto · <span className="font-normal text-sm">{cashSession.operator_name || "operador"}</span>
              </div>
              <div className="text-xs text-emerald-800 flex flex-wrap gap-x-3 gap-y-0.5">
                <span>Esperado: <b>{brl(cashSession.expected_balance || 0)}</b></span>
                <span>Abertura: {brl(cashSession.opening_balance || 0)}</span>
                {cashSession.card_fees_total > 0 && (
                  <span>Taxas: {brl(cashSession.card_fees_total)}</span>
                )}
              </div>
            </div>
          </div>
          <Button
            onClick={openCloseFlow}
            variant="outline"
            className="rounded-full border-emerald-600 text-emerald-800 hover:bg-emerald-100"
            data-testid="pos-close-cash-btn">
            <CheckCheck className="w-4 h-4 mr-1" /> Fechar caixa
          </Button>
        </div>
      )}

      <div className="grid lg:grid-cols-[1fr_380px] gap-4 min-h-[70vh]">
        {/* Products grid */}
        <div className="space-y-3">
          <div className="flex gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
              <Input className="pl-9" placeholder="Buscar produto por nome ou SKU..." value={q} onChange={e=>setQ(e.target.value)} data-testid="pos-search" data-search />
            </div>
            <Select value={catFilter} onValueChange={setCatFilter}>
              <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas categorias</SelectItem>
                {cats.map(c=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {filtered.map(p=>(
              <button key={p.id} onClick={()=>add(p)} data-testid={`prod-tile-${p.id}`}
                className="bg-white border rounded-xl p-4 text-left hover:border-emerald-600 hover:bg-emerald-50 transition">
                <div className="font-semibold truncate">{p.name}</div>
                <div className="text-xs text-muted-foreground truncate">{cats.find(c=>c.id===p.category_id)?.name || "—"}</div>
                <div className="mt-2 font-bold text-emerald-700">{brl(p.price)}</div>
              </button>
            ))}
            {filtered.length===0 && <p className="text-muted-foreground col-span-full">Nenhum produto. Cadastre em <b>Produtos</b>.</p>}
          </div>
        </div>

        {/* Cart */}
        <Card className="border-neutral-200 sticky top-2 flex flex-col max-h-[calc(100dvh-8rem)]"><CardContent className="p-5 flex flex-col flex-1 min-h-0 overflow-hidden">
          {/* ── zona scrollável: itens, totais, opções ── */}
          <div className="flex-1 overflow-y-auto min-h-0">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold flex items-center gap-2"><ShoppingCart className="w-4 h-4"/>Carrinho</h3>
            {cart.length > 0 && <button className="text-xs text-muted-foreground" onClick={clear}>Limpar</button>}
          </div>

          <div className="space-y-2 mb-3">
            <Select value={customerId || "none"} onValueChange={(v)=>setCustomerId(v==="none"?"":v)}>
              <SelectTrigger data-testid="pos-customer"><SelectValue placeholder="Selecionar cliente (opcional)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sem cliente</SelectItem>
                {customers.map(c=><SelectItem key={c.id} value={c.id}>{c.name} ({c.phone})</SelectItem>)}
              </SelectContent>
            </Select>
            {selectedCust && <div className="text-xs bg-emerald-50 border border-emerald-200 rounded p-2 flex items-center gap-2"><User className="w-3 h-3 text-emerald-700"/>{selectedCust.name} • Saldo {brl(selectedCust.cashback_balance)}</div>}
          </div>

          <div className="space-y-2 mb-3">
            {cart.length===0 && <div className="text-center text-muted-foreground py-12 text-sm">Adicione produtos ao carrinho</div>}
            {cart.map(i => {
              const prod = products.find(pp => pp.id === i.product_id);
              const hasTipItem = prod?.has_tip;
              const dineInQty = itemSplits[i.product_id] ?? i.quantity;
              const takeawayQty = +(i.quantity - dineInQty).toFixed(3);
              return (
                <div key={i.product_id} className="bg-neutral-50 rounded-lg" data-testid={`cart-${i.product_id}`}>
                  <div className="flex items-center gap-2 p-2">
                    <div className="flex-1 min-w-0"><div className="font-medium truncate">{i.name}</div><div className="text-xs text-muted-foreground">{brl(i.price)} cada</div></div>
                    <div className="flex items-center gap-1">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={()=>setQty(i.product_id, i.quantity-1)}><Minus className="w-3 h-3"/></Button>
                      <span className="w-6 text-center text-sm font-medium">{i.quantity}</span>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={()=>setQty(i.product_id, i.quantity+1)}><Plus className="w-3 h-3"/></Button>
                    </div>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={()=>remove(i.product_id)}><Trash2 className="w-3 h-3"/></Button>
                  </div>
                  {loadedTab && hasTipItem && (
                    <div className="flex items-center gap-1.5 px-2 pb-2 text-xs text-muted-foreground border-t border-neutral-200">
                      <span className="flex-1 text-amber-700">Gorjeta da mesa:</span>
                      <input
                        type="number" min={0} max={i.quantity} step={1}
                        className="w-12 h-6 border rounded text-center text-xs px-1 bg-white"
                        value={dineInQty}
                        data-testid={`dest-dine-${i.product_id}`}
                        onChange={e => {
                          const v = Math.min(Math.max(0, Number(e.target.value)), i.quantity);
                          setItemSplits(s => ({ ...s, [i.product_id]: v }));
                        }}
                      />
                      <span>| viagem: {takeawayQty}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {selectedCust && (
            <div className="space-y-2 mb-3">
              <div><Label className="text-xs">Usar cashback (máx {brl(maxCashback)})</Label><Input type="number" step="0.01" min="0" max={maxCashback} value={useCashback} onChange={e=>setUseCashback(e.target.value)} data-testid="pos-use-cashback" /></div>
              <div><Label className="text-xs">Cupom</Label><Input value={couponCode} onChange={e=>setCouponCode(e.target.value.toUpperCase())} placeholder="LOOP2026" /></div>
            </div>
          )}

          {/* Consumo do colaborador — % fixo, não negociável, só aparece se configurado */}
          {cart.length > 0 && hasEmployeeDiscount && (
            <div className="mb-3 rounded-lg border border-neutral-200 p-2.5 bg-neutral-50/50" data-testid="pos-employee-purchase-block">
              <button
                type="button"
                onClick={() => setEmployeePurchase(v => !v)}
                className={`w-full rounded-md px-2 py-1.5 text-xs font-medium border transition ${employeePurchase ? "bg-rose-600 text-white border-rose-600" : "bg-white text-neutral-700 border-neutral-300"}`}
                data-testid="employee-purchase-toggle"
              >
                {employeePurchase ? `Consumo do colaborador (${fixedDiscountPct}%) ativo` : `Marcar como consumo do colaborador (${fixedDiscountPct}%)`}
              </button>
            </div>
          )}

          {/* Manual discount at checkout — % or R$, pro cliente (ex: troco insuficiente) */}
          {cart.length > 0 && !employeePurchase && (
            <div className="mb-3 rounded-lg border border-neutral-200 p-2.5 bg-neutral-50/50" data-testid="pos-discount-block">
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <Label className="text-xs font-medium">Desconto no fechamento</Label>
                <div className="inline-flex rounded-full bg-neutral-200 p-0.5 text-[11px]">
                  <button type="button"
                    onClick={() => setDiscountMode("value")}
                    className={`px-2 py-0.5 rounded-full transition ${discountMode === "value" ? "bg-white shadow-sm" : "text-neutral-600"}`}
                    data-testid="discount-mode-value">R$</button>
                  <button type="button"
                    onClick={() => setDiscountMode("percent")}
                    className={`px-2 py-0.5 rounded-full transition ${discountMode === "percent" ? "bg-white shadow-sm" : "text-neutral-600"}`}
                    data-testid="discount-mode-percent">%</button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="relative">
                  <Input
                    type="number" step="0.01" min="0"
                    value={discountInput}
                    onChange={(e) => setDiscountInput(e.target.value)}
                    placeholder={discountMode === "percent" ? "Ex: 10" : "Ex: 5,00"}
                    className="pr-6"
                    data-testid="discount-input"
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                    {discountMode === "percent" ? "%" : "R$"}
                  </span>
                </div>
                <Input
                  value={discountReason}
                  onChange={(e) => setDiscountReason(e.target.value)}
                  placeholder={posUserRole === "owner" ? "Motivo (opcional)" : "Motivo (obrigatório)"}
                  maxLength={120}
                  className={discountReasonRequired ? "border-rose-500" : ""}
                  data-testid="discount-reason"
                />
              </div>
              {manualDiscount > 0 && (
                <div className="text-[11px] text-rose-700 mt-1.5">
                  Aplicando desconto de {brl(manualDiscount)}
                  {discountMode === "percent" && discountRaw > 0 ? ` (${pctBr(discountRaw, 2)})` : ""}
                  {manualDiscount === total && total > 0 ? " (venda zerada)" : ""}
                </div>
              )}
              {discountReasonRequired && (
                <div className="text-[11px] text-rose-600 mt-1">Informe o motivo do desconto.</div>
              )}
            </div>
          )}
          {cart.length > 0 && employeePurchase && manualDiscount > 0 && (
            <div className="text-[11px] text-rose-700 mb-3">Aplicando desconto de {brl(manualDiscount)} (consumo do colaborador)</div>
          )}

          <div className="space-y-1 mb-3">
            <div className="flex justify-between text-sm"><span>Subtotal</span><span>{brl(total)}</span></div>
            {manualDiscount > 0 && (
              <div className="flex justify-between text-sm text-rose-700" data-testid="discount-line">
                <span>Desconto{discountMode === "percent" && discountRaw > 0 ? ` (${pctBr(discountRaw, 2)})` : ""}</span>
                <span>-{brl(manualDiscount)}</span>
              </div>
            )}
            {useCashback > 0 && <div className="flex justify-between text-sm text-emerald-700"><span>Cashback aplicado</span><span>-{brl(useCashback)}</span></div>}
            {hasTippable && (
              <div className="pt-1 border-t" data-testid="tip-section">
                <div className="text-xs text-muted-foreground mb-1 flex items-center justify-between gap-1">
                  <span>💵 Gorjeta (apenas sobre produtos com gorjeta: {brl(tippableSubtotal)})</span>
                </div>
                <div className="flex gap-1 flex-wrap">
                  {isPosStaff ? (
                    /* Staff: apenas Sem gorjeta ou % padrão do estabelecimento */
                    [0, tipSettings.default_percent].map(p => (
                      <button key={p} type="button" onClick={()=>setTipPct(p)}
                        className={`px-3 h-8 rounded-full text-xs font-medium border transition ${tipPct===p?"bg-amber-500 text-white border-amber-500":"bg-white hover:bg-amber-50"}`}
                        data-testid={`tip-pct-${p}`}>
                        {p===0?"Sem":`${p}%`}
                      </button>
                    ))
                  ) : (
                    /* Owner/Manager: presets + campo livre */
                    <>
                      {/* O preset de 15% saiu: está acima do teto de 10% e o
                          backend recusaria a venda no fechamento. Os presets
                          são filtrados pelo teto, não escritos à mão, para que
                          um default_percent mal configurado não reintroduza a
                          opção. */}
                      {[0, 5, tipSettings.default_percent, GORJETA_TETO_PCT]
                        .filter(v => v <= GORJETA_TETO_PCT)
                        .filter((v,i,a)=>a.indexOf(v)===i)
                        .sort((a,b)=>a-b)
                        .map(p => (
                        <button key={p} type="button" onClick={()=>setTipPct(p)}
                          className={`px-3 h-8 rounded-full text-xs font-medium border transition ${tipPct===p?"bg-amber-500 text-white border-amber-500":"bg-white hover:bg-amber-50"}`}
                          data-testid={`tip-pct-${p}`}>
                          {p===0?"Sem":`${p}%`}
                        </button>
                      ))}
                      <Input type="number" step="0.5" min="0" max={GORJETA_TETO_PCT}
                        placeholder={`% (máx ${GORJETA_TETO_PCT})`} value={tipPct}
                        onChange={e=>setTipPct(Math.min(parseFloat(e.target.value)||0, GORJETA_TETO_PCT))}
                        className="h-8 w-28 text-xs" data-testid="tip-custom" />
                    </>
                  )}
                </div>
                {tipAmount > 0 && <div className="flex justify-between text-sm text-amber-700 mt-1"><span>Gorjeta ({tipPct}%)</span><span>+{brl(tipAmount)}</span></div>}
                {/* O teto pegou — quase sempre por causa do desconto. Dizer o
                    motivo evita que o atendente ache que a gorjeta "sumiu". */}
                {tipCapped && (
                  <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1" data-testid="tip-capped-notice">
                    Gorjeta limitada a {brl(tipCap)}. O máximo é {GORJETA_TETO_PCT}% da conta
                    {manualDiscount > 0 ? " depois do desconto" : ""}.
                  </div>
                )}
              </div>
            )}
            <div className="flex justify-between text-lg font-bold"><span>Total</span><span>{brl(net)}</span></div>
          </div>

          {!loadedTab && (
            <div className="mb-3" data-testid="pos-more-options">
              <button
                type="button"
                onClick={() => setMoreOptionsOpen(v => !v)}
                className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                data-testid="pos-toggle-more-options"
              >
                {moreOptionsOpen ? "▾" : "▸"} Mais opções
                {!moreOptionsOpen && (peopleCount > 1 || channel !== "balcao") && (
                  <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-primary-muted text-primary-muted-foreground">
                    {peopleCount > 1 ? `${peopleCount} pessoas` : ""}
                    {peopleCount > 1 && channel !== "balcao" ? " · " : ""}
                    {channel !== "balcao" ? channel : ""}
                  </span>
                )}
              </button>
              {moreOptionsOpen && (
                <div className="grid grid-cols-2 gap-2 mt-2" data-testid="pos-ops-meta">
                  <div>
                    <Label className="text-xs">Pessoas atendidas</Label>
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      value={peopleCount}
                      onChange={e => setPeopleCount(Math.max(1, parseInt(e.target.value) || 1))}
                      data-testid="pos-people-count"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Canal</Label>
                    <Select value={channel} onValueChange={setChannel}>
                      <SelectTrigger data-testid="pos-channel"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="balcao">Balcão</SelectItem>
                        <SelectItem value="mesa">Mesa</SelectItem>
                        <SelectItem value="delivery">Delivery</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          )}

          </div>{/* fim zona scrollável */}

          {/* ── rodapé fixo: pagamento + Finalizar ── */}
          <div className="shrink-0 border-t pt-3 mt-1 space-y-2">
          <Select value={paymentMethod} onValueChange={setPaymentMethod} disabled={splitPayers.length > 0}>
            <SelectTrigger data-testid="pos-payment" className={!paymentMethod && splitPayers.length === 0 ? "border-amber-400 ring-1 ring-amber-300" : ""}>
              <SelectValue placeholder="Selecione a forma de pagamento" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="cash">Dinheiro</SelectItem>
              <SelectItem value="card">Cartão</SelectItem>
              <SelectItem value="pix">Pix</SelectItem>
              <SelectItem value="cashback">Cashback</SelectItem>
            </SelectContent>
          </Select>

          {isCashPayment && (
            <div className="mt-2 space-y-1.5" data-testid="pos-cash-detail">
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  inputMode="decimal"
                  placeholder="Valor recebido do cliente"
                  value={cashReceived}
                  onChange={e => setCashReceived(e.target.value)}
                  data-testid="pos-cash-received"
                  className="flex-1"
                />
                <Button type="button" variant="outline" size="sm" onClick={() => setCashReceived(net.toFixed(2))} data-testid="pos-cash-exact">
                  Valor exato
                </Button>
              </div>
              {cashReceivedNum > 0 && (
                changeDue >= 0 ? (
                  <div className="text-sm font-medium text-emerald-700" data-testid="pos-cash-change">
                    Troco: {brl(changeDue)}
                  </div>
                ) : (
                  <div className="text-sm font-medium text-rose-600" data-testid="pos-cash-insufficient">
                    Faltam {brl(-changeDue)}. Valor recebido insuficiente
                  </div>
                )
              )}
            </div>
          )}

          {paymentMethod === "card" && splitPayers.length === 0 && (
            <div className="grid grid-cols-2 gap-2 mt-2" data-testid="pos-card-detail">
              <Select value={cardKind} onValueChange={setCardKind}>
                <SelectTrigger data-testid="pos-card-kind"><SelectValue placeholder="Tipo" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="credit">Crédito</SelectItem>
                  <SelectItem value="debit">Débito</SelectItem>
                </SelectContent>
              </Select>
              <Select value={cardBrand} onValueChange={setCardBrand}>
                <SelectTrigger data-testid="pos-card-brand"><SelectValue placeholder="Bandeira" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="visa">Visa</SelectItem>
                  <SelectItem value="master">Mastercard</SelectItem>
                  <SelectItem value="elo">Elo</SelectItem>
                  <SelectItem value="amex">Amex</SelectItem>
                  <SelectItem value="hipercard">Hipercard</SelectItem>
                  <SelectItem value="outro">Outro</SelectItem>
                  {(JSON.parse(localStorage.getItem("oplyx_user") || "{}")?.settings?.card_brands || []).map(b => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {paymentMethod === "card" && splitPayers.length === 0 && activeAcquirers.length >= 2 && (
            <div className="mt-2" data-testid="pos-acquirer-select">
              <div className="text-xs text-muted-foreground mb-1.5">Qual maquininha?</div>
              <div className="flex flex-col gap-1">
                {activeAcquirers.map(a => (
                  <label key={a.id}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition
                      ${selectedAcquirerId === a.id
                        ? "border-emerald-500 bg-emerald-50"
                        : "border-neutral-200 bg-white hover:bg-neutral-50"}`}>
                    <input type="radio" className="accent-emerald-600" name="pos-acquirer"
                      checked={selectedAcquirerId === a.id}
                      onChange={() => setSelectedAcquirerId(a.id)} />
                    <span className="flex-1 font-medium">{a.name}</span>
                    {a.is_default && (
                      <span className="text-[10px] bg-neutral-100 text-neutral-500 px-1.5 py-0.5 rounded">padrão</span>
                    )}
                  </label>
                ))}
              </div>
              {selectedAcquirer && cardFeePct > 0 && (
                <div className="text-[11px] text-muted-foreground mt-1.5">
                  Taxa {selectedAcquirer.name}: {cardFeePct}% = –{brl(cardFeeAmount)}
                </div>
              )}
            </div>
          )}

          <Button variant={splitPayers.length > 0 ? "default" : "outline"} size="default" className={`mt-2 w-full rounded-full font-medium ${splitPayers.length > 0 ? "bg-violet-600 hover:bg-violet-700 text-white" : "border-violet-400 text-violet-700 hover:bg-violet-50"}`}
                  data-testid="pos-split-btn"
                  onClick={() => setSplitOpen(true)} disabled={!cart.length}>
            <CreditCard className="w-4 h-4 mr-1" />
            {splitPayers.length === 0 ? "Dividir em várias formas de pagamento" : `Pagamento dividido em ${splitPayers.length} (${brl(splitPayers.reduce((s,p)=>s+p.amount,0))})`}
          </Button>
          {splitPayers.length > 0 && (
            <button type="button" onClick={() => setSplitPayers([])} className="text-[11px] text-muted-foreground hover:text-rose-600 mt-1 w-full text-center" data-testid="pos-split-clear">
              ✕ Remover divisão
            </button>
          )}

          <Button onClick={checkout}
                  disabled={!cart.length || isSubmitting || cashInsufficient || (!paymentMethod && splitPayers.length === 0)}
                  className="rounded-full h-12 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="pos-checkout">
            {isSubmitting
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Processando…</>
              : <><CreditCard className="w-4 h-4 mr-2"/>{loadedTab ? `Fechar comanda ${brl(net)}` : `Finalizar ${brl(net)}`}</>}
          </Button>
          </div>{/* fim rodapé fixo */}
        </CardContent></Card>
      </div>

      <SplitPaymentDialog
        open={splitOpen}
        onClose={() => setSplitOpen(false)}
        total={subtotalAfterDiscount + tipAmount}
        tipAmount={tipAmount}
        cartItems={cart}
        initial={splitPayers}
        onApply={(payers) => { setSplitPayers(payers); setSplitOpen(false); }}
      />

      <Dialog open={!!lastSale} onOpenChange={(v)=>{ if(!v){ setLastSale(null); setNfceDoc(null); setNfceMode("online"); } }}>
        <DialogContent data-testid="post-sale-dialog">
          <DialogHeader>
            <DialogTitle>
              {lastSale?._tab
                ? `Comanda "${lastSale._tabLabel}" fechada${lastSale._offline ? " offline" : ""}: ${brl(lastSale?.total || 0)}`
                : `Venda finalizada: ${brl(lastSale?.total || 0)}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {lastSale?.customer && (
              <div className="text-xs text-muted-foreground">
                Cliente: <b>{lastSale.customer.name}</b>
              </div>
            )}

            {lastSale?.change > 0 && (
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-center" data-testid="post-sale-change">
                <div className="text-xs text-emerald-700">Troco a devolver</div>
                <div className="text-2xl font-bold text-emerald-700">{brl(lastSale.change)}</div>
              </div>
            )}

            {/* ── NFC-e: bloco principal ── */}
            {lastSale?._offline ? (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm flex items-start gap-2">
                <WifiOff className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
                <div>
                  <b>Venda em contingência.</b> A NFC-e será transmitida ao SEFAZ automaticamente quando a conexão for restabelecida.
                </div>
              </div>
            ) : (
              <div className="border border-neutral-200 rounded-lg p-3 space-y-2">
                {/* Emitindo (antes do nfceDoc chegar) */}
                {nfceLoading && !nfceDoc && (
                  <div className="rounded-md p-2 text-xs flex items-center gap-2 bg-amber-50 text-amber-700 border border-amber-200">
                    <Loader2 className="w-3.5 h-3.5 animate-spin"/>Emitindo NFC-e…
                  </div>
                )}
                {/* Status badge */}
                {nfceDoc && (
                  <div className={`rounded-md p-2 text-xs flex items-center gap-2 ${
                    nfceDoc.status === "authorized" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
                    nfceDoc.status === "contingency" ? "bg-amber-50 text-amber-800 border border-amber-200" :
                    nfceDoc.status === "rejected"   ? "bg-red-50 text-red-700 border border-red-200" :
                    nfceDoc.status === "error"      ? "bg-red-50 text-red-700 border border-red-200" :
                    nfceDoc._timedOut               ? "bg-neutral-50 text-neutral-600 border border-neutral-200" :
                    "bg-amber-50 text-amber-700 border border-amber-200"
                  }`} data-testid="nfce-status-badge">
                    {nfceDoc.status === "authorized" && <><FileText className="w-3.5 h-3.5"/>NFC-e autorizada pela SEFAZ</>}
                    {nfceDoc.status === "rejected"   && <><AlertTriangle className="w-3.5 h-3.5"/>Rejeitada: {typeof nfceDoc.rejection_reason === "string" && nfceDoc.rejection_reason ? nfceDoc.rejection_reason : nfceDoc.rejection_reason != null ? JSON.stringify(nfceDoc.rejection_reason) : "erro desconhecido"}</>}
                    {nfceDoc.status === "contingency" && <><FileText className="w-3.5 h-3.5"/>NFC-e emitida em contingência. Será transmitida à SEFAZ automaticamente</>}
                    {nfceDoc.status === "error"      && <><AlertTriangle className="w-3.5 h-3.5"/>Erro: {typeof nfceDoc.rejection_reason === "string" && nfceDoc.rejection_reason ? nfceDoc.rejection_reason : nfceDoc.rejection_reason != null ? JSON.stringify(nfceDoc.rejection_reason) : "erro desconhecido"}</>}
                    {nfceDoc.status === "pending" && !nfceDoc._timedOut && <><Loader2 className="w-3.5 h-3.5 animate-spin"/>Aguardando SEFAZ…</>}
                    {nfceDoc.status === "pending" && nfceDoc._timedOut  && <><FileText className="w-3.5 h-3.5"/>NFC-e em processamento. Consulte o relatório de vendas.</>}
                  </div>
                )}
                {/* Ações */}
                {NFCE_IMPRIMIVEL.has(nfceDoc?.status) && nfceDoc.id ? (
                  <Button
                    onClick={() => openNfceCupom(nfceDoc.id)}
                    className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8"
                    data-testid="print-danfe-btn"
                  >
                    <FileText className="w-3.5 h-3.5 mr-1.5"/>Imprimir NFC-e
                  </Button>
                ) : (nfceDoc?.status === "error" || nfceDoc?.status === "rejected") ? (
                  <Button
                    onClick={() => emitNfce()}
                    disabled={nfceLoading}
                    className="w-full rounded-full bg-sky-700 hover:bg-sky-600 text-white text-xs h-8"
                    data-testid="emit-nfce-retry-btn"
                  >
                    {nfceLoading
                      ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/>Emitindo…</>
                      : <><FileText className="w-3.5 h-3.5 mr-1.5"/>Tentar novamente</>
                    }
                  </Button>
                ) : null}
              </div>
            )}

            <Button variant="ghost" onClick={()=>{ setLastSale(null); setNfceDoc(null); setNfceMode("online"); }} className="w-full text-xs" data-testid="close-post-sale">Fechar</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={openCashOpen} onOpenChange={setOpenCashOpen}>
        <DialogContent data-testid="open-cash-dialog">
          <DialogHeader><DialogTitle>Abrir caixa</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Informe o valor de abertura (troco em dinheiro, opcional). Sem caixa aberto,
              nenhuma venda ou comanda pode ser finalizada.
            </p>
            <div>
              <Label>Valor de abertura</Label>
              <Input type="number" step="0.01" value={openingAmount}
                     onChange={e => setOpeningAmount(e.target.value)}
                     placeholder="0.00" data-testid="open-cash-amount" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenCashOpen(false)}>Cancelar</Button>
            <Button onClick={openCash} className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="open-cash-submit">Abrir caixa</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CashCloseFlow
        cashSession={cashSession}
        step={closeStep}
        actualByMethod={closeActualByMethod}
        setActualByMethod={setCloseActualByMethod}
        justification={closeJustification}
        setJustification={setCloseJustification}
        closing={closing}
        result={closeResult}
        onReset={resetCloseFlow}
        onConferenceConfirm={handleConferenceConfirm}
        onReconferir={() => setCloseStep("conference")}
        onBackToDivergence={() => setCloseStep("divergence")}
        onCloseWithDivergence={() => setCloseStep("divergence_confirm")}
        onConfirmDivergence={() => _doClose(true)}
      />
    </div>
  );
}


// =============== CASH CLOSE FLOW ===============

const METHOD_LABELS = {
  money: "Dinheiro", pix: "Pix",
  credit_visa: "Crédito Visa", credit_master: "Crédito Mastercard",
  credit_elo: "Crédito Elo", credit_amex: "Crédito Amex",
  credit_hipercard: "Crédito Hipercard", credit_outro: "Crédito Outro",
  debit_visa: "Débito Visa", debit_master: "Débito Mastercard",
  debit_elo: "Débito Elo", debit_hipercard: "Débito Hipercard",
  debit_outro: "Débito Outro", voucher: "Voucher", outros: "Outros",
};
const methodLabel = (k) =>
  METHOD_LABELS[k] || k.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

function fmtDT(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}

function CashCloseFlow({
  cashSession, step, actualByMethod, setActualByMethod,
  justification, setJustification, closing, result,
  onReset, onConferenceConfirm, onReconferir, onBackToDivergence,
  onCloseWithDivergence, onConfirmDivergence,
}) {
  const expectedMethods = cashSession?.expected_by_method || {};
  const methodKeys = Object.keys(expectedMethods);

  const totalSystem = methodKeys.reduce((s, k) => s + (parseFloat(expectedMethods[k]) || 0), 0);
  const totalConferido = methodKeys.reduce((s, k) => s + (parseFloat(actualByMethod[k]) || 0), 0);
  const globalDiff = totalConferido - totalSystem;
  const allFilled = methodKeys.length > 0 && methodKeys.every(k => actualByMethod[k] !== undefined && String(actualByMethod[k]).trim() !== "");

  const openReport = (id) => window.open(`/financial/cash-closure/${id}`, "_blank");
  const open80mm  = (id) => window.open(`/financial/cash-closure/${id}?paper=80mm&autoprint=1`, "print80mm", "width=440,height=700");
  const openPdf   = async (id) => {
    try {
      const resp = await api.get(`/financial/cash/${id}/conference-report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch { toast.error("Erro ao gerar PDF"); }
  };

  // ── Etapa 1 — Conferência ─────────────────────────────────────────────────
  if (step === "conference") return (
    <Dialog open onOpenChange={(open) => !open && onReset()}>
      <DialogContent
        data-testid="pos-close-conference-dialog"
        className="max-w-3xl w-full"
        style={{ maxHeight: "90vh", overflowY: "auto" }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Wallet className="w-5 h-5 text-emerald-600" /> Conferência de caixa
          </DialogTitle>
        </DialogHeader>

        {/* Cabeçalho info */}
        <div className="rounded-lg bg-neutral-50 border px-4 py-3 flex flex-wrap gap-4 justify-between text-sm">
          <div>
            <div className="text-xs text-muted-foreground">Operador</div>
            <div className="font-medium">{cashSession?.operator_name || "—"}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Abertura</div>
            <div className="font-medium">{fmtDT(cashSession?.opened_at)}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Saldo inicial</div>
            <div className="font-medium">{brl(cashSession?.opening_balance || 0)}</div>
          </div>
        </div>

        {/* Dois blocos lado a lado */}
        <div className="grid md:grid-cols-2 gap-4 mt-1">
          {/* Bloco esquerdo — Sistema (somente leitura) */}
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Valores do sistema
            </div>
            <div className="rounded-lg border divide-y overflow-hidden">
              {methodKeys.map(k => (
                <div key={k} className="flex items-center justify-between px-3 py-2.5 text-sm bg-white">
                  <span className="text-neutral-700">{methodLabel(k)}</span>
                  <span className="font-mono font-medium tabular-nums">{brl(expectedMethods[k] || 0)}</span>
                </div>
              ))}
              <div className="flex items-center justify-between px-3 py-2.5 text-sm font-semibold bg-neutral-50">
                <span>Total do sistema</span>
                <span className="font-mono tabular-nums">{brl(totalSystem)}</span>
              </div>
            </div>
          </div>

          {/* Bloco direito — Conferido (editável) */}
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Valores conferidos
            </div>
            <div className="rounded-lg border divide-y overflow-hidden">
              {methodKeys.map(k => (
                <div key={k} className="flex items-center gap-2 px-3 py-2 bg-white" data-testid={`pos-close-row-${k}`}>
                  <span className="text-sm text-neutral-700 flex-1 min-w-0 truncate">{methodLabel(k)}</span>
                  <Input
                    type="number" step="0.01" min="0"
                    inputMode="decimal"
                    className="w-28 text-right h-9 tabular-nums text-sm"
                    placeholder="0,00"
                    value={actualByMethod[k] ?? ""}
                    onChange={e => setActualByMethod(s => ({ ...s, [k]: e.target.value }))}
                    data-testid={`pos-close-input-${k}`}
                  />
                </div>
              ))}
              <div className="flex items-center justify-between px-3 py-2.5 text-sm font-semibold bg-neutral-50">
                <span>Total conferido</span>
                <span className="font-mono tabular-nums">{brl(totalConferido)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Linha de divergência global */}
        <div className={`rounded-lg px-4 py-3 flex items-center justify-between text-sm font-medium border mt-1 ${
          allFilled
            ? Math.abs(globalDiff) < 0.01
              ? "bg-emerald-50 border-emerald-300 text-emerald-800"
              : "bg-rose-50 border-rose-300 text-rose-800"
            : "bg-neutral-50 border-neutral-200 text-neutral-500"
        }`} data-testid="pos-close-diff-banner">
          {allFilled ? (
            Math.abs(globalDiff) < 0.01 ? (
              <><CheckCircle2 className="w-4 h-4 mr-2 text-emerald-600" /> Caixa conferido</>
            ) : (
              <><AlertTriangle className="w-4 h-4 mr-2 text-rose-600" />
                Divergência de {globalDiff > 0 ? "+" : ""}{brl(globalDiff)}</>
            )
          ) : (
            <span className="text-neutral-400">Preencha todos os campos para ver a diferença</span>
          )}
        </div>

        {/* Justificativa opcional */}
        <div>
          <Label className="text-xs text-muted-foreground">Observação / justificativa (opcional)</Label>
          <Input
            value={justification}
            onChange={e => setJustification(e.target.value)}
            placeholder="Ex: falta troco de R$ 2,00"
            className="mt-1"
            data-testid="pos-close-justification"
          />
        </div>

        <DialogFooter className="gap-2 mt-2">
          <Button variant="ghost" onClick={onReset} disabled={closing} data-testid="pos-close-cancel">
            <X className="w-4 h-4 mr-1" /> Cancelar
          </Button>
          <Button
            onClick={onConferenceConfirm}
            disabled={!allFilled || closing}
            className="bg-emerald-600 hover:bg-emerald-700 min-h-[48px]"
            data-testid="pos-close-confirm-btn"
          >
            {closing
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Fechando…</>
              : <><CheckCheck className="w-4 h-4 mr-2" /> Confirmar fechamento</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // ── Etapa 2A — Sucesso (caixa batido) ────────────────────────────────────
  if (step === "success" && result) {
    const resDiffs = result.diffs_by_method || {};
    const hasAnyDiff = Object.values(resDiffs).some(v => Math.abs(v) >= 0.01);
    return (
      <Dialog open onOpenChange={(open) => !open && onReset()}>
        <DialogContent
          data-testid="pos-close-success-dialog"
          className="max-w-2xl w-full"
          style={{ maxHeight: "90vh", overflowY: "auto" }}
        >
          {/* Status banner */}
          <div className={`rounded-xl flex flex-col items-center justify-center py-6 gap-2 ${
            hasAnyDiff ? "bg-rose-50 border border-rose-200" : "bg-emerald-50 border border-emerald-200"
          }`} data-testid="pos-close-status-banner">
            {hasAnyDiff
              ? <AlertTriangle className="w-12 h-12 text-rose-500" />
              : <CheckCircle2 className="w-12 h-12 text-emerald-500" />}
            <div className={`text-lg font-bold ${hasAnyDiff ? "text-rose-800" : "text-emerald-800"}`}>
              {hasAnyDiff ? `Fechado com divergência de ${brl(result.difference || 0)}` : "Caixa fechado sem divergências"}
            </div>
          </div>

          {/* Resumo */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            {[
              ["Operador", result.operator_name || "—"],
              ["Fechado por", result.closed_by_name || "—"],
              ["Abertura", fmtDT(result.opened_at)],
              ["Fechamento", fmtDT(result.closed_at)],
              ["Total do sistema", brl(result.expected_balance || 0)],
              ["Total conferido", brl(result.actual_balance || 0)],
            ].map(([label, val]) => (
              <div key={label} className="rounded-lg border bg-neutral-50 p-3">
                <div className="text-xs text-muted-foreground">{label}</div>
                <div className="font-semibold mt-0.5">{val}</div>
              </div>
            ))}
          </div>

          {/* Breakdown por forma de pagamento */}
          <div className="rounded-lg border overflow-hidden text-sm">
            <div className="bg-neutral-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Resumo por forma de pagamento
            </div>
            <div className="divide-y">
              {Object.entries(result.expected_by_method || {}).map(([k, exp]) => (
                <div key={k} className="flex items-center justify-between px-3 py-2" data-testid={`pos-result-row-${k}`}>
                  <span className="text-neutral-700">{methodLabel(k)}</span>
                  <span className="font-mono tabular-nums font-medium">{brl(exp)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Botões de impressão */}
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => openReport(result.id)} data-testid="pos-close-print-a4">
              <Printer className="w-4 h-4 mr-2" /> Imprimir A4
            </Button>
            <Button variant="outline" size="sm" onClick={() => open80mm(result.id)} data-testid="pos-close-print-80mm">
              <Receipt className="w-4 h-4 mr-2" /> Imprimir 80mm
            </Button>
            <Button variant="outline" size="sm" onClick={() => openPdf(result.id)} data-testid="pos-close-download-pdf">
              <Download className="w-4 h-4 mr-2" /> Baixar PDF
            </Button>
          </div>

          <DialogFooter>
            <Button onClick={onReset} className="bg-emerald-600 hover:bg-emerald-700 min-h-[48px]" data-testid="pos-close-done-btn">
              <CheckCheck className="w-4 h-4 mr-2" /> Concluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // ── Etapa 2B — Divergência ────────────────────────────────────────────────
  if (step === "divergence") {
    const expMethods = cashSession?.expected_by_method || {};
    return (
      <Dialog open onOpenChange={(open) => !open && onReset()}>
        <DialogContent
          data-testid="pos-close-divergence-dialog"
          className="max-w-2xl w-full"
          style={{ maxHeight: "90vh", overflowY: "auto" }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="w-5 h-5" /> Divergência encontrada
            </DialogTitle>
          </DialogHeader>

          <div className="rounded-lg bg-rose-50 border border-rose-200 px-4 py-3 text-sm font-medium text-rose-800 flex items-center gap-2"
               data-testid="pos-divergence-banner">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            Divergência de {globalDiff > 0 ? "+" : ""}{brl(globalDiff)}:
            {globalDiff > 0 ? " sobra no caixa físico" : " falta no caixa físico"}
          </div>

          {/* Tabela de divergências por método */}
          <div className="rounded-lg border overflow-hidden text-sm">
            <table className="w-full">
              <thead className="bg-neutral-50 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2">Forma de pagamento</th>
                  <th className="text-right px-3 py-2">Sistema</th>
                  <th className="text-right px-3 py-2">Conferido</th>
                  <th className="text-right px-3 py-2">Diferença</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {Object.keys(expMethods).map(k => {
                  const exp = parseFloat(expMethods[k]) || 0;
                  const act = parseFloat(actualByMethod[k]) || 0;
                  const diff = +(act - exp).toFixed(2);
                  return (
                    <tr key={k} className={Math.abs(diff) >= 0.01 ? "bg-rose-50/50" : ""} data-testid={`pos-div-row-${k}`}>
                      <td className="px-3 py-2">{methodLabel(k)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{brl(exp)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{brl(act)}</td>
                      <td className={`px-3 py-2 text-right tabular-nums font-medium ${
                        Math.abs(diff) < 0.01 ? "text-neutral-400" : diff > 0 ? "text-emerald-600" : "text-rose-600"
                      }`}>
                        {Math.abs(diff) < 0.01 ? "—" : `${diff > 0 ? "+" : ""}${brl(diff)}`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-neutral-50 font-semibold text-sm">
                <tr>
                  <td className="px-3 py-2">Total</td>
                  <td className="px-3 py-2 text-right tabular-nums">{brl(totalSystem)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{brl(totalConferido)}</td>
                  <td className={`px-3 py-2 text-right tabular-nums ${globalDiff > 0 ? "text-emerald-600" : "text-rose-600"}`}>
                    {globalDiff > 0 ? "+" : ""}{brl(globalDiff)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={onReconferir} disabled={closing} data-testid="pos-divergence-recheck">
              <ArrowLeft className="w-4 h-4 mr-2" /> Reconferir
            </Button>
            <Button
              variant="destructive"
              onClick={onCloseWithDivergence}
              disabled={closing}
              className="min-h-[48px]"
              data-testid="pos-divergence-force-close"
            >
              <AlertTriangle className="w-4 h-4 mr-2" /> Fechar com divergência
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // ── Etapa 3 — Confirmação de fechamento com divergência ───────────────────
  if (step === "divergence_confirm") return (
    <Dialog open onOpenChange={(open) => !open && !closing && onBackToDivergence()}>
      <DialogContent data-testid="pos-close-divergence-confirm-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <AlertTriangle className="w-5 h-5" /> Confirmar fechamento com divergência
          </DialogTitle>
        </DialogHeader>
        <div className="text-sm text-neutral-700 space-y-2">
          <p>
            Tem certeza? O caixa será fechado com <span className="font-semibold text-rose-700">divergência registrada</span> e
            a diferença de <span className="font-semibold">{brl(globalDiff)}</span> ficará no histórico de auditoria.
          </p>
          <p className="text-xs text-muted-foreground">Esta ação não pode ser desfeita.</p>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onBackToDivergence} disabled={closing} data-testid="pos-div-confirm-cancel">
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirmDivergence}
            disabled={closing}
            className="min-h-[48px]"
            data-testid="pos-div-confirm-ok"
          >
            {closing
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Fechando…</>
              : "Confirmar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return null;
}

// =============== SPLIT PAYMENT DIALOG ===============
function SplitPaymentDialog({ open, onClose, total, initial, onApply, cartItems = [], tipAmount = 0 }) {
  const userBrands = JSON.parse(localStorage.getItem("oplyx_user") || "{}")?.settings?.card_brands || [];
  const BRANDS = ["visa", "master", "elo", "amex", "hipercard", "outro", ...userBrands];

  const [mode, setMode]           = useState("value");
  const [payers, setPayers]       = useState([]);
  const [people, setPeople]       = useState([]);
  const [assignments, setAssignments] = useState({});

  // Expande itens do carrinho por quantidade para atribuição individual
  const expandedItems = useMemo(() =>
    cartItems.flatMap((item, ci) =>
      Array.from({ length: Math.max(1, item.quantity) }, (_, k) => ({
        product_id: item.product_id,
        name:       item.name,
        price:      parseFloat(item.price) || 0,
        key:        `${ci}_${k}`,
      }))
    ), [cartItems]);

  const rawSubtotal = useMemo(
    () => expandedItems.reduce((s, i) => s + i.price, 0),
    [expandedItems]
  );

  useEffect(() => {
    if (!open) return;
    setMode("value");
    setPayers(initial.length ? initial : [
      { amount: (total / 2).toFixed(2), payment_method: "cash", label: "Pessoa 1" },
      { amount: (total / 2).toFixed(2), payment_method: "cash", label: "Pessoa 2" },
    ]);
    setPeople([
      { id: 1, label: "Pessoa 1", payment_method: "cash", card_kind: "credit", card_brand: "visa" },
      { id: 2, label: "Pessoa 2", payment_method: "cash", card_kind: "credit", card_brand: "visa" },
    ]);
    setAssignments({});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Modo valor ───────────────────────────────────────────────────
  const sum    = payers.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
  const diff   = +(total - sum).toFixed(2);
  const okSum  = Math.abs(diff) < 0.01;

  const updPayer   = (i, k, v) => setPayers(s => s.map((p, idx) => idx === i ? { ...p, [k]: v } : p));
  const addPayer   = () => setPayers(s => [...s, { amount: 0, payment_method: "cash", label: `Pessoa ${s.length + 1}` }]);
  const remPayer   = (i) => setPayers(s => s.filter((_, idx) => idx !== i));
  const splitEqual = () => {
    const n    = payers.length || 2;
    const each = +(total / n).toFixed(2);
    const last = +(total - each * (n - 1)).toFixed(2);
    setPayers(p => p.map((x, idx) => ({ ...x, amount: idx === n - 1 ? last : each })));
  };

  // ── Modo produto ─────────────────────────────────────────────────
  const addPerson = () => setPeople(p => {
    const id = Date.now();
    return [...p, { id, label: `Pessoa ${p.length + 1}`, payment_method: "cash", card_kind: "credit", card_brand: "visa" }];
  });
  const remPerson = (id) => {
    setPeople(p => p.filter(x => x.id !== id));
    setAssignments(a => {
      const n = { ...a };
      Object.keys(n).forEach(k => { if (n[k] === id) delete n[k]; });
      return n;
    });
  };
  const updPerson  = (id, k, v) => setPeople(p => p.map(x => x.id === id ? { ...x, [k]: v } : x));
  const assign     = (key, pid) => setAssignments(a => ({ ...a, [key]: pid }));
  const assignAll  = (pid) => setAssignments(Object.fromEntries(expandedItems.map(i => [i.key, pid])));

  const personTotals = useMemo(() => people.map(person => {
    const mine    = expandedItems.filter(i => assignments[i.key] === person.id);
    const myRaw   = mine.reduce((s, i) => s + i.price, 0);
    const share   = rawSubtotal > 0 ? myRaw / rawSubtotal : 0;
    const myTotal = +(share * total).toFixed(2);
    const myTip   = +(share * tipAmount).toFixed(2);
    return { id: person.id, myTotal, myTip, mySub: +(myTotal - myTip).toFixed(2) };
  }), [people, assignments, expandedItems, rawSubtotal, total, tipAmount]);

  const unassigned = expandedItems.filter(i => !assignments[i.key]).length;
  const okProduct  = unassigned === 0 && expandedItems.length > 0;

  // ── Apply ────────────────────────────────────────────────────────
  const applyValue = () => {
    if (!okSum) return toast.error(`Soma (${brl(sum)}) difere do total (${brl(total)})`);
    const cleaned = payers
      .map(p => ({
        amount:         parseFloat(p.amount) || 0,
        payment_method: p.payment_method,
        ...(p.payment_method === "card" ? { card_kind: p.card_kind || "credit", card_brand: p.card_brand || "visa" } : {}),
        label: (p.label || "").trim() || null,
      }))
      .filter(p => p.amount > 0);
    if (!cleaned.length) return toast.error("Adicione ao menos um pagador");
    onApply(cleaned);
  };

  const applyProduct = () => {
    if (!okProduct) return toast.error(`${unassigned} ${unassigned === 1 ? "item não atribuído" : "itens não atribuídos"}`);
    const withItems = people.filter(p => (personTotals.find(pt => pt.id === p.id)?.myTotal || 0) > 0);
    let acc = 0;
    const legs = withItems.map((person, idx) => {
      const pt     = personTotals.find(pt => pt.id === person.id);
      // Último pagador recebe o restante exato para evitar diferença de centavos
      const amount = idx === withItems.length - 1
        ? Math.round((total - acc) * 100) / 100
        : pt.myTotal;
      acc += amount;
      return {
        amount,
        payment_method: person.payment_method,
        ...(person.payment_method === "card" ? { card_kind: person.card_kind || "credit", card_brand: person.card_brand || "visa" } : {}),
        label: (person.label || "").trim() || null,
      };
    });
    if (!legs.length) return toast.error("Nenhuma pessoa com itens atribuídos");
    onApply(legs);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="split-dialog" className="max-w-2xl flex flex-col" style={{ maxHeight: "90vh" }}>
        <DialogHeader>
          <DialogTitle>Dividir pagamento, total {brl(total)}</DialogTitle>
        </DialogHeader>

        {/* Seletor de modo — só aparece quando há itens no carrinho */}
        {cartItems.length > 0 && (
          <div className="flex gap-1 bg-neutral-100 rounded-lg p-1 shrink-0">
            <button onClick={() => setMode("value")}
              className={`flex-1 text-sm py-1.5 rounded-md font-medium transition ${mode === "value" ? "bg-white shadow text-neutral-900" : "text-neutral-500 hover:text-neutral-700"}`}>
              Por valor
            </button>
            <button onClick={() => setMode("product")}
              className={`flex-1 text-sm py-1.5 rounded-md font-medium transition ${mode === "product" ? "bg-white shadow text-neutral-900" : "text-neutral-500 hover:text-neutral-700"}`}>
              Por produto
            </button>
          </div>
        )}

        <div className="overflow-y-auto flex-1 min-h-0 space-y-3 py-1">

          {/* ── MODO VALOR ─────────────────────────────────────────── */}
          {mode === "value" && (
            <>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={addPayer} data-testid="split-add">+ Pagador</Button>
                <Button size="sm" variant="outline" onClick={splitEqual} data-testid="split-equal">Dividir igual</Button>
                <div className="ml-auto text-sm">
                  Soma: <b className={okSum ? "text-emerald-600" : "text-rose-600"}>{brl(sum)}</b>
                  {!okSum && <span className="text-xs text-rose-600 ml-2">(dif: {brl(diff)})</span>}
                </div>
              </div>
              {payers.map((p, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`split-row-${i}`}>
                  <Input className="col-span-3" value={p.label || ""} onChange={e => updPayer(i, "label", e.target.value)} placeholder="Nome" data-testid={`split-label-${i}`} />
                  <Input className="col-span-2 text-right" type="number" step="0.01" min="0" value={p.amount} onChange={e => updPayer(i, "amount", e.target.value)} data-testid={`split-amount-${i}`} />
                  <Select value={p.payment_method} onValueChange={v => updPayer(i, "payment_method", v)}>
                    <SelectTrigger className="col-span-2" data-testid={`split-method-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Dinheiro</SelectItem>
                      <SelectItem value="pix">Pix</SelectItem>
                      <SelectItem value="card">Cartão</SelectItem>
                    </SelectContent>
                  </Select>
                  {p.payment_method === "card" ? (
                    <>
                      <Select value={p.card_kind || "credit"} onValueChange={v => updPayer(i, "card_kind", v)}>
                        <SelectTrigger className="col-span-2" data-testid={`split-kind-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="credit">Crédito</SelectItem>
                          <SelectItem value="debit">Débito</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={p.card_brand || "visa"} onValueChange={v => updPayer(i, "card_brand", v)}>
                        <SelectTrigger className="col-span-2" data-testid={`split-brand-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{BRANDS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                      </Select>
                    </>
                  ) : <div className="col-span-4" />}
                  <Button size="icon" variant="ghost" className="col-span-1 text-rose-600" onClick={() => remPayer(i)} data-testid={`split-remove-${i}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </>
          )}

          {/* ── MODO PRODUTO ────────────────────────────────────────── */}
          {mode === "product" && (
            <div className="space-y-3">
              {/* Cards de pessoas */}
              {people.map(person => {
                const pt = personTotals.find(pt => pt.id === person.id);
                return (
                  <div key={person.id} className="border rounded-lg p-2.5 bg-neutral-50 space-y-2">
                    <div className="flex gap-2 items-center flex-wrap">
                      <Input value={person.label} onChange={e => updPerson(person.id, "label", e.target.value)}
                        className="h-7 text-sm font-medium w-28 shrink-0" placeholder="Nome" />
                      <Select value={person.payment_method} onValueChange={v => updPerson(person.id, "payment_method", v)}>
                        <SelectTrigger className="h-7 w-28 text-xs shrink-0"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cash">Dinheiro</SelectItem>
                          <SelectItem value="pix">Pix</SelectItem>
                          <SelectItem value="card">Cartão</SelectItem>
                        </SelectContent>
                      </Select>
                      {person.payment_method === "card" && (
                        <>
                          <Select value={person.card_kind || "credit"} onValueChange={v => updPerson(person.id, "card_kind", v)}>
                            <SelectTrigger className="h-7 w-24 text-xs shrink-0"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="credit">Crédito</SelectItem>
                              <SelectItem value="debit">Débito</SelectItem>
                            </SelectContent>
                          </Select>
                          <Select value={person.card_brand || "visa"} onValueChange={v => updPerson(person.id, "card_brand", v)}>
                            <SelectTrigger className="h-7 w-24 text-xs shrink-0"><SelectValue /></SelectTrigger>
                            <SelectContent>{BRANDS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                          </Select>
                        </>
                      )}
                      <button className="text-xs text-blue-600 hover:underline ml-auto shrink-0"
                        onClick={() => assignAll(person.id)}>Tudo para mim</button>
                      {people.length > 1 && (
                        <Button size="icon" variant="ghost" className="h-6 w-6 text-rose-500 shrink-0"
                          onClick={() => remPerson(person.id)}>
                          <X className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                    {pt && pt.myTotal > 0 && (
                      <div className="text-xs text-right tabular-nums text-neutral-600">
                        {tipAmount > 0 && (
                          <span className="mr-2">Produtos: {brl(pt.mySub)} · Gorjeta: {brl(pt.myTip)} ·</span>
                        )}
                        <b className="text-neutral-900">Total: {brl(pt.myTotal)}</b>
                      </div>
                    )}
                  </div>
                );
              })}
              <Button size="sm" variant="outline" className="w-full" onClick={addPerson}>+ Pessoa</Button>

              {/* Lista de itens para atribuir */}
              <div className="border rounded-lg divide-y overflow-y-auto max-h-52">
                {expandedItems.map(item => (
                  <div key={item.key} className="flex items-center gap-2 px-3 py-2">
                    <span className="flex-1 text-sm truncate">{item.name}</span>
                    <span className="text-xs text-neutral-400 tabular-nums shrink-0">{brl(item.price)}</span>
                    <Select value={assignments[item.key] ? String(assignments[item.key]) : ""}
                            onValueChange={v => assign(item.key, Number(v))}>
                      <SelectTrigger className={`h-7 w-28 text-xs shrink-0 ${!assignments[item.key] ? "border-amber-300 text-amber-700" : ""}`}>
                        <SelectValue placeholder="Quem paga?" />
                      </SelectTrigger>
                      <SelectContent>
                        {people.map(p => <SelectItem key={p.id} value={String(p.id)}>{p.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>

              {unassigned > 0 && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-1.5">
                  ⚠ {unassigned} {unassigned === 1 ? "item não atribuído" : "itens não atribuídos"}. Atribua todos para continuar
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0 pt-2">
          {initial.length > 0 && <Button variant="ghost" onClick={() => onApply([])} data-testid="split-clear">Limpar</Button>}
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          {mode === "value"
            ? <Button onClick={applyValue} disabled={!okSum} data-testid="split-apply">Aplicar divisão</Button>
            : <Button onClick={applyProduct} disabled={!okProduct} data-testid="split-apply-product">Aplicar divisão</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
